// Client layer (TS-2.5.03) - D-266/D-267's admin/management UI for the product catalogue.
// configuration.manage-gated the same way every other Admin tab is: AdminPage's own route-level
// RequireRole (App.tsx) is the authoritative gate, and this panel adds no second check, on the
// same convention ReferenceDataPanel/FieldMapPanel state in their own headers.
//
// RETIRE IS CONFIRMED, REACTIVATE IS NOT - the same asymmetry ReferenceDataPanel/FieldMapPanel
// already draw: a confirm is for an act whose effect reaches past the row it was made on (D-268:
// retiring removes a product from every picker, portfolio-wide), while reactivating puts it back
// exactly where it was. There is no delete anywhere on this screen because there is none on the
// wire (D-268) - a product leaves service only by being saved with isActive false.
import { useState } from "react";
import { ApiError } from "../../api/ApiError";
import type { ProductRecord } from "../../api/generated/contracts";
import { productsApi } from "../../api/products";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import { FormField } from "../../components/FormField";
import { formFieldControlProps } from "../../components/formFieldControlProps";
import { SuggestionInput } from "../../components/SuggestionInput";
import type { SuggestionInputOption } from "../../components/SuggestionInput";
import { useProducts } from "../../hooks/useProducts";
import { useOwnerSuggestions } from "./hooks/useOwnerSuggestions";
import { draftFromProduct, EMPTY_PRODUCT_DRAFT, productFromDraft } from "./lib/productDraft";
import type { ProductDraft } from "./lib/productDraft";
import { strings } from "../../strings";

const F = strings.admin.products;

export function ProductsPanel() {
  const [includeRetired, setIncludeRetired] = useState(true);
  const { products, loading, error, refresh } = useProducts(includeRetired);
  const { owners } = useOwnerSuggestions();

  const [showAdd, setShowAdd] = useState(false);
  const [addDraft, setAddDraft] = useState<ProductDraft>(EMPTY_PRODUCT_DRAFT);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState<ProductDraft>(EMPTY_PRODUCT_DRAFT);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<ApiError | null>(null);

  const [retiringKey, setRetiringKey] = useState<string | null>(null);

  async function save(product: ProductRecord, concurrencyToken: string | null) {
    setSaving(true);
    setSaveError(null);
    try {
      const result = product.key
        ? await productsApi.updateProduct(product.key, { product, concurrencyToken })
        : await productsApi.createProduct({ product, concurrencyToken });

      if (result.saved) {
        setShowAdd(false);
        setEditingKey(null);
        refresh();
      }
    } catch (cause) {
      setSaveError(cause instanceof ApiError ? cause : new ApiError("network", 0, "The product could not be saved."));
    } finally {
      setSaving(false);
    }
  }

  async function confirmRetire(product: ProductRecord) {
    setRetiringKey(null);
    await save({ ...product, isActive: false }, product.concurrencyToken);
  }

  async function reactivate(product: ProductRecord) {
    await save({ ...product, isActive: true }, product.concurrencyToken);
  }

  const retiringProduct = products?.find((product) => product.key === retiringKey) ?? null;

  return (
    <section className="eg-admin-products">
      <h2>{F.heading}</h2>

      <div className="eg-admin__toolbar">
        <label>
          <input
            type="checkbox"
            checked={includeRetired}
            onChange={(event) => setIncludeRetired(event.target.checked)}
          />
          {F.includeRetired}
        </label>

        {!showAdd && (
          <div className="eg-admin__toolbar-actions">
            <button
              type="button"
              className="eg-admin__btn--primary"
              onClick={() => {
                setAddDraft(EMPTY_PRODUCT_DRAFT);
                setShowAdd(true);
              }}
            >
              {F.addProduct}
            </button>
          </div>
        )}
      </div>

      {loading && !products && (
        <div role="status" aria-live="polite">
          {F.loading}
        </div>
      )}

      {error && (
        <div role="alert">
          <p>{F.errorTitle}</p>
          <p>{error.message}</p>
        </div>
      )}

      {products && products.length === 0 && <p>{F.empty}</p>}

      {products && products.length > 0 && (
        <table>
          <caption>{F.heading}</caption>
          <thead>
            <tr>
              <th scope="col">{F.name}</th>
              <th scope="col">{F.technologyOwner}</th>
              <th scope="col">{F.businessOwner}</th>
              <th scope="col">{F.active}</th>
              <th scope="col" />
            </tr>
          </thead>
          <tbody>
            {products.map((product) => (
              <tr key={product.key ?? product.name}>
                {editingKey === product.key ? (
                  <td colSpan={5}>
                    <form
                      className="eg-admin__form"
                      onSubmit={(event) => {
                        event.preventDefault();
                        void save(productFromDraft(editDraft), editDraft.concurrencyToken);
                      }}
                    >
                      <ProductForm idPrefix={`eg-admin-product-edit-${product.key}`} draft={editDraft} onChange={(patch) => setEditDraft((current) => ({ ...current, ...patch }))} owners={owners} />
                      <div className="eg-admin__actions">
                        <button type="submit" className="eg-admin__btn--primary" disabled={saving}>
                          {saving ? F.saving : F.save}
                        </button>
                        <button type="button" disabled={saving} onClick={() => setEditingKey(null)}>
                          {F.cancel}
                        </button>
                      </div>
                    </form>
                  </td>
                ) : (
                  <>
                    <td>{product.name}</td>
                    <td>{product.technologyOwner ?? "—"}</td>
                    <td>{product.businessOwner ?? "—"}</td>
                    <td>{product.isActive ? F.active : "—"}</td>
                    <td>
                      <button
                        type="button"
                        onClick={() => {
                          setEditDraft(draftFromProduct(product));
                          setEditingKey(product.key);
                        }}
                      >
                        {F.edit}
                      </button>
                      {product.isActive ? (
                        <button type="button" className="eg-admin__btn--danger" onClick={() => setRetiringKey(product.key)}>
                          {F.retire}
                        </button>
                      ) : (
                        <button type="button" onClick={() => void reactivate(product)}>
                          {F.reactivate}
                        </button>
                      )}
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <ConfirmDialog
        open={retiringProduct !== null}
        title={F.retireConfirmTitle}
        body={F.retireConfirmBody}
        confirmLabel={F.retireConfirmConfirm}
        cancelLabel={F.retireConfirmCancel}
        onConfirm={() => {
          if (retiringProduct) void confirmRetire(retiringProduct);
        }}
        onCancel={() => setRetiringKey(null)}
      />

      {saveError && (
        <div role="alert">
          <p>{F.errorSaveTitle}</p>
          <p>{saveError.message}</p>
        </div>
      )}

      {showAdd && (
        <form
          className="eg-admin__form"
          onSubmit={(event) => {
            event.preventDefault();
            void save(productFromDraft(addDraft), null);
          }}
        >
          <ProductForm idPrefix="eg-admin-product-add" draft={addDraft} onChange={(patch) => setAddDraft((current) => ({ ...current, ...patch }))} owners={owners} />
          <div className="eg-admin__actions">
            <button type="submit" className="eg-admin__btn--primary" disabled={saving}>
              {saving ? F.saving : F.addProduct}
            </button>
            <button type="button" disabled={saving} onClick={() => setShowAdd(false)}>
              {F.cancel}
            </button>
          </div>
        </form>
      )}
    </section>
  );
}

interface ProductFormProps {
  readonly idPrefix: string;
  readonly draft: ProductDraft;
  readonly onChange: (patch: Partial<ProductDraft>) => void;
  readonly owners: readonly { readonly displayName: string; readonly projectCount: number }[];
}

function ProductForm({ idPrefix, draft, onChange, owners }: ProductFormProps) {
  // Owner ruling 2026-10-03, step two: Technology owner / Business owner stay OPEN free-text
  // fields, so each is a SuggestionInput rather than a VocabularySelect - see that component's
  // header for why a plain `<datalist>` (filtered by the current text) was the bug being fixed.
  // Both share one suggestion pool, the same as ProjectForm's Owner/Business owner.
  const ownerSuggestions: readonly SuggestionInputOption[] = owners.map((owner) => ({
    value: owner.displayName,
    label: owner.displayName,
    hint: `(${owner.projectCount})`,
  }));

  return (
    <>
      <FormField id={`${idPrefix}-name`} label={F.name} required>
        <input
          type="text"
          required
          value={draft.name}
          onChange={(event) => onChange({ name: event.target.value })}
          {...formFieldControlProps(`${idPrefix}-name`)}
        />
      </FormField>
      <FormField id={`${idPrefix}-tech-owner`} label={F.technologyOwner}>
        <SuggestionInput
          id={`${idPrefix}-tech-owner`}
          value={draft.technologyOwner}
          onChange={(next) => onChange({ technologyOwner: next })}
          suggestions={ownerSuggestions}
        />
      </FormField>
      <FormField id={`${idPrefix}-business-owner`} label={F.businessOwner}>
        <SuggestionInput
          id={`${idPrefix}-business-owner`}
          value={draft.businessOwner}
          onChange={(next) => onChange({ businessOwner: next })}
          suggestions={ownerSuggestions}
        />
      </FormField>
    </>
  );
}
